import { useState, useMemo, useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import { Stack, Typography, Card, CardContent, Grid, CircularProgress, } from "@mui/material";

import dayjs from "dayjs";
import DrmTable from "../tmt/DrmTable";
import TableActionButtons from "../tmt/TableActionButtons";
import { DRM_COLUMN } from "../../constData/columnDrmt";
import { pageSize, downloadZipFiles } from "../../helpers";
import { getCeaProfileList } from "../../redux/reducers/drmCeaProfileListSlice";
import { ceaProfileFilterPayload } from "../../constData/filterRequestPayloadCeaProfile";
import { setCeaProfileFilter, resetCeaColumnTableFilters } from "../../redux/reducers/drmCeaProfileTableFilterSlice";
import CustomButton from "../common/CustomButton";
import CustomSmallerIconDatePicker from "../common/CustomSmallerIconDatePicker";
import CustomSnackbar from "../common/CustomSnackbar";
import DialogConformationMsg from "../tmt/DialogConformationMsg";
import { downloadCeaProfileFilesStart, resetCeaProfileDownloadState } from "../../redux/reducers/drmCeaProfileDownloadSlice";
import { useInactiveCeaDownload } from "../../customHook/useInactiveCeaDownload";
import { useDrmFilterUrlSync } from "../../customHook/useDrmFilterUrlSync";

import "./CoupaFIleDeleteDetails.sass";

const INITIAL_TABLE_STATE = {
  originalData: [],
  headers: [],
  toggleProps: null,
  density: "small",
  currentPage: 1,
  updatedData: [],
  selectedRows: [],
  modifiedRows: {},
  newAddedRows: [],
};


const CEA_PROFILE_COLUMNS = ["isDownloaded", "uploadedDate", "dataStoredUnit", "fileSize", "fileNames"];

const normalizeCeaProfileRequest = (payload) => {
  const clonedPayload = JSON.parse(JSON.stringify(payload || {}));
  const filters = clonedPayload.filters || {};

  return {
    ...clonedPayload,
    filters,
  };
};

function CeaProfileDownload() {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { ceaProfileList, isLoading: isCeaProfileLoading, onPage, totalPages, totalRecords } = useSelector((state) => state.drmCeaProfileList);

  const { ceaProfileFilter } = useSelector((state) => state.drmCeaProfileFilterPayload);
  const { isLoading: isDownloadLoading, blob: downloadBlob, successMessage: downloadSuccessMessage, errorMessage: downloadErrorMessage } = useSelector((state) => state.drmCeaProfileDownload);

  const [tableState, setTableState] = useState(INITIAL_TABLE_STATE);
  const [selectedRows, setSelectedRows] = useState([]);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [snackbarState, setSnackbarState] = useState({ open: false, message: "", severity: "success" });
  const [downloadDialogOpen, setDownloadDialogOpen] = useState(false);

  const inactive = useInactiveCeaDownload(fromDate, toDate);

  const cloneFilters = useCallback((filters) => JSON.parse(JSON.stringify(filters ?? {})), []);

  useDrmFilterUrlSync({
    scope: "ceaProfile",
    filterState: ceaProfileFilter,
    dispatch,
    setFilterAction: setCeaProfileFilter,
    uiState: null,
    setUiState: () => {},
    initialUiState: null,
    defaultFilterState: ceaProfileFilterPayload,
    countryOptions: [],
  });

  const requestBody = useMemo(
    () => normalizeCeaProfileRequest(cloneFilters(ceaProfileFilter || ceaProfileFilterPayload)),
    [cloneFilters, ceaProfileFilter]
  );

  const fetchCeaProfileList = useCallback(
    (page = 1) => {
      dispatch(
        getCeaProfileList({
          pageSize,
          pageNum: page,
          tableName: "drmCeaProfileList",
          reqBody: requestBody,
        })
      );
    },
    [requestBody]
  );

  useEffect(() => {
    const existingDates = ceaProfileFilter?.filters?.startDate?.date || [];
    const existingEndDates = ceaProfileFilter?.filters?.endDate?.date || [];
    setFromDate(existingDates[0] ? new Date(existingDates[0]) : null);
    setToDate(existingEndDates[0] ? new Date(existingEndDates[0]) : null);
  }, [ceaProfileFilter]);

  const updateDateFilter = useCallback(
    (start, end) => {
      const formattedStart = start ? dayjs(start).format("MM/DD/YYYY") : null;
      const formattedEnd = end ? dayjs(end).format("MM/DD/YYYY") : null;

      dispatch(
        setCeaProfileFilter({
          ...ceaProfileFilter,
          filters: {
            ...(ceaProfileFilter?.filters || {}),
            startDate: { date: formattedStart ? [formattedStart] : [], operator: ">=" },
            endDate: { date: formattedEnd ? [formattedEnd] : [], operator: "<=" },
          },
        })
      );
    },
    [ceaProfileFilter]
  );

  const tableData = useMemo(() => ceaProfileList || [], [ceaProfileList]);

  const handlePageRefresh = useCallback(
    (page) => {
      const targetPage = page ?? tableState.currentPage ?? 1;
      setTableState((prev) => ({ ...prev, currentPage: targetPage }));
      fetchCeaProfileList(targetPage);
    },
    [fetchCeaProfileList, tableState.currentPage]
  );

  const handleClearTableFilters = useCallback(() => {
    dispatch(resetCeaColumnTableFilters());
    handlePageRefresh(1);
  }, [handlePageRefresh]);

  const handleSearch = useCallback(() => {
    handlePageRefresh(1);
  }, [handlePageRefresh]);

  const handleFromDateChange = useCallback(
    ({ date }) => {
      const newFrom = date || null;
      setFromDate(newFrom);
      updateDateFilter(newFrom, toDate);
    },
    [updateDateFilter, toDate]
  );

  const handleToDateChange = useCallback(
    ({ date }) => {
      const newTo = date || null;
      setToDate(newTo);
      updateDateFilter(fromDate, newTo);
    },
    [updateDateFilter, fromDate]
  );

  const columnOrder = useMemo(
    () =>
      CEA_PROFILE_COLUMNS.map((columnName) => DRM_COLUMN.find((col) => col.Header === columnName)).filter(Boolean),
    []
  );

  const selectedCeaIds = useMemo(() => {
    const ids = (selectedRows || [])
      .map((row) => {
        const original = row?.original || {};
        return original?.id ?? original?.ceaProfileId ?? null;
      })
      .filter((id) => id !== null && id !== undefined);

    return Array.from(new Set(ids));
  }, [selectedRows]);

  const hasSelectedRows = selectedCeaIds.length > 0;

  const handleSelectionChange = useCallback((rows) => {
    setSelectedRows(rows || []);
  }, []);

  const handleSnackbarClose = useCallback(() => {
    setSnackbarState((prev) => ({ ...prev, open: false }));
  }, []);

  const openDownloadDialog = useCallback(() => {
    if (!hasSelectedRows) {
      setSnackbarState({ open: true, message: t("select_rows_to_download"), severity: "warning" });
      return;
    }
    setDownloadDialogOpen(true);
  }, [hasSelectedRows]);

  const handleDownloadDialogClose = useCallback(() => {
    setDownloadDialogOpen(false);
  }, []);

  const handleDownloadConfirm = useCallback(() => {
    const formattedStartDate = fromDate ? dayjs(fromDate).format("MM/DD/YYYY") : null;
    const formattedEndDate = toDate ? dayjs(toDate).format("MM/DD/YYYY") : null;

    const downloadPayload = {
      filters: {
        startDate: { date: formattedStartDate ? [formattedStartDate] : [], operator: ">=" },
        endDate: { date: formattedEndDate ? [formattedEndDate] : [], operator: "<=" },
        fileNames: [],
        uploadedDate: ceaProfileFilter?.filters?.uploadedDate ?? { date: [], operator: "=" },
        id: selectedCeaIds,
        fileSize: ceaProfileFilter?.filters?.fileSize ?? { operator: "", params: [] },
        isDownloaded: ceaProfileFilter?.filters?.isDownloaded ?? [],
      },
    };

    dispatch(downloadCeaProfileFilesStart(downloadPayload));
    setDownloadDialogOpen(false);
  }, [selectedCeaIds, fromDate, toDate, ceaProfileFilter]);

  useEffect(() => {
    if (downloadSuccessMessage && downloadBlob) {
      downloadZipFiles({ data: downloadBlob });
      setSnackbarState({ open: true, message: downloadSuccessMessage, severity: "success" });
      handlePageRefresh(tableState.currentPage);
      setSelectedRows([]);
      dispatch(resetCeaProfileDownloadState());
    }
  }, [downloadSuccessMessage, downloadBlob, handlePageRefresh, tableState.currentPage]);

  useEffect(() => {
    if (downloadErrorMessage) {
      setSnackbarState({ open: true, message: downloadErrorMessage, severity: "error" });
      dispatch(resetCeaProfileDownloadState());
    }
  }, [downloadErrorMessage]);

  return (
    <Stack spacing={2} alignItems="flex-start" width="100%" px={2}>
      <Stack alignItems="flex-start">
        <Typography variant="h4" gutterBottom>
          {t("cea_profile_download")}
        </Typography>
      </Stack>

      <Card sx={{ width: "100%" }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <CustomSmallerIconDatePicker
                label={t("from_date")}
                value={fromDate}
                onChange={handleFromDateChange}
                maxDate={toDate || undefined}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <CustomSmallerIconDatePicker
                label={t("to_date")}
                value={toDate}
                onChange={handleToDateChange}
                minDate={fromDate || undefined}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={3}>
              <CustomButton
                fullWidth
                className="dark-blue-btn"
                onClick={handleSearch}
                datatestid="coupa-date-search"
                size="small"
                btnType="nonary"
              >
                {t("search")}
              </CustomButton>
            </Grid>
            <Grid item xs={12} sm={12} md={3}>
              <CustomButton
                fullWidth
                className="dark-blue-btn"
                onClick={inactive.handleDownload}
                datatestid="process-inactive-lane-btn"
                size="small"
                btnType="nonary"
                disabled={!inactive.hasDates || inactive.isDownloading}
                startIcon={inactive.isDownloading ? <CircularProgress size={16} color="inherit" /> : undefined}
              >
               {t("Process Inactive Lane Constraints")}
               
              </CustomButton>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      
      <TableActionButtons
        tableState={tableState}
        setTableState={setTableState}
        handleRefresh={handlePageRefresh}
      />
      <DrmTable
        tableName="CEA Profile"
        tableData={tableData}
        columnOrder={columnOrder}
        tableState={tableState}
        editableRow={false}
        allowRowSelection
        onSelectionChange={handleSelectionChange}
        onPage={onPage}
        totalPages={totalPages}
        isLoadingDrmTable={isCeaProfileLoading}
        totalRecords={totalRecords}
        setTableState={setTableState}
        handleRefresh={handlePageRefresh}
        handleClear={handleClearTableFilters}
      />

      <Stack direction="row" justifyContent="flex-end" spacing={1} width="100%" px={0}>
        <CustomButton
          datatestid="download-cea-files"
          size="small"
          variant="outlined"
          btnType="nonary"
          onClick={openDownloadDialog}
          disabled={isDownloadLoading || !hasSelectedRows}
          className="coupa-action-button"
          aria-label="download-cea-files"
        >
          {t("download")}
        </CustomButton>
      </Stack>

      {snackbarState.open && (
        <CustomSnackbar
          open={snackbarState.open}
          severity={snackbarState.severity}
          autoHideDuration={5000}
          datatestId="cea-download-feedback"
          message={snackbarState.message}
          handleClose={handleSnackbarClose}
        />
      )}

      {inactive.snackbar.open && (
        <CustomSnackbar
          open={inactive.snackbar.open}
          severity={inactive.snackbar.severity}
          autoHideDuration={5000}
          datatestId="inactive-cea-feedback"
          message={inactive.snackbar.message}
          handleClose={inactive.handleSnackbarClose}
        />
      )}

      <DialogConformationMsg
        open={downloadDialogOpen}
        handleClose={handleDownloadDialogClose}
        handleConfirm={handleDownloadConfirm}
        dialogTitle={t("download_cea_dialog_title")}
        dialogContentText={t("download_cea_selected_content")}
        cancel={t("cancel")}
        confirm={t("download")}
        confirmLoading={isDownloadLoading}
      />

    </Stack>
  );
}

export default CeaProfileDownload;
 